/** Lux lots on the dashboard (Lux & billing › Details; backend services/lux_wallet.py `lot_models`): labels and
 * expiry wording. Amounts stay integers (hundredths of a Lux, cents); dates are only compared and formatted. */

import type { LuxWords } from "./lux.ts";

export type LotKind = "pack" | "grant" | "bonus" | "refund";
export type LotLike = { kind: LotKind; source: string; note?: string | null; reserved?: boolean };

const DAY_MS = 86_400_000;

/** "Pack", "Pack · auto top-up", "Grant", "Bonus · switch program · AI designs only", "Bonus · promo code",
 * "Refund". A lot reserved for automatic designs says so. */
export function lotLabel(lot: LotLike, w?: LuxWords): { title: string; detail: string | null } {
  if (w) return wordsLabel(lot, w);
  const base = baseLabel(lot);
  if (!lot.reserved) return base;
  return { title: base.title, detail: base.detail ? `${base.detail} · AI designs only` : "AI designs only" };
}

/** The same labels from the `lux` catalog. */
function wordsLabel(lot: LotLike, { t }: LuxWords): { title: string; detail: string | null } {
  const title = t("lux.lot.kind", { kind: lot.kind });
  const detail = (() => {
    switch (lot.source) {
      case "auto_top_up":
        return t("lux.lot.source.autoTopUp");
      case "grant":
        return t("lux.lot.source.grant");
      case "switch_bonus":
        return t("lux.lot.source.switchBonus");
      case "bonus":
        return t("lux.lot.source.promo");
      case "refund":
        return lot.note?.includes("expired") ? t("lux.lot.source.refundExpired") : t("lux.lot.source.refundAdvance");
      default:
        return null;
    }
  })();
  if (!lot.reserved) return { title, detail };
  return { title, detail: detail ? t("lux.lot.reservedWith", { detail }) : t("lux.lot.reserved") };
}

function baseLabel(lot: LotLike): { title: string; detail: string | null } {
  switch (lot.source) {
    case "purchase":
      return { title: "Pack", detail: null };
    case "auto_top_up":
      return { title: "Pack", detail: "auto top-up" };
    case "grant":
      return { title: "Grant", detail: "from Solar OS" };
    case "switch_bonus":
      return { title: "Bonus", detail: "switch program" };
    case "bonus":
      return { title: "Bonus", detail: "promo code" };
    case "refund":
      return { title: "Refund", detail: lot.note?.includes("expired") ? "of expired Lux" : "of advanced Lux" };
    default:
      return { title: lot.kind.charAt(0).toUpperCase() + lot.kind.slice(1), detail: null };
  }
}

/** Whole days from `now` until `at` (rounded up; 0 when it is due today or past). */
export function daysUntil(at: string, now: number = Date.now()): number {
  const ms = new Date(at).getTime() - now;
  return ms <= 0 ? 0 : Math.ceil(ms / DAY_MS);
}

/** "today", "tomorrow", "in 12 days", "in 3 months" (about), "in about a year". */
export function untilText(at: string, now: number = Date.now(), w?: LuxWords): string {
  const days = daysUntil(at, now);
  if (w) {
    if (days <= 0) return w.t("lux.until.today");
    if (days === 1) return w.t("lux.until.tomorrow");
    if (days < 60) return w.t("lux.until.days", { days });
    const m = Math.round(days / 30.4);
    return m >= 12 ? w.t("lux.until.year") : w.t("lux.until.months", { months: m });
  }
  if (days <= 0) return "today";
  if (days === 1) return "tomorrow";
  if (days < 60) return `in ${days} days`;
  const months = Math.round(days / 30.4);
  return months >= 12 ? "in about a year" : `in ${months} months`;
}

/** Expiring within 30 days: the dashboard marks it. */
export function isSoon(at: string, now: number = Date.now()): boolean {
  return daysUntil(at, now) <= 30;
}
